import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider, useDispatch, useSelector } from "react-redux";
import Store from "./redux/store";
import { loadTheme } from "./redux/actions/theme";
import "font-awesome/css/font-awesome.min.css";
import { getStoredAuthToken } from "./utils/authSession";
import PrivateLaunchGate from "./components/PrivateLaunchGate";

const isInternalBuzzVibeRequest = (requestUrl) => {
  if (typeof window === "undefined") {
    return false;
  }

  if (typeof requestUrl !== "string" || requestUrl.trim() === "") {
    return false;
  }

  if (requestUrl.startsWith("/")) {
    return true;
  }

  try {
    const resolvedUrl = new URL(requestUrl, window.location.origin);
    const requestHostname = String(resolvedUrl.hostname || "").toLowerCase();
    const currentHostname = String(window.location.hostname || "").toLowerCase();

    return (
      requestHostname === currentHostname ||
      /(^|\.)buzzvibe\.com$/i.test(requestHostname) ||
      (/\.vercel\.app$/i.test(requestHostname) && /(buzzvibe|buzz-vibe)/i.test(requestHostname))
    );
  } catch (error) {
    return false;
  }
};

let hasConfiguredAxiosAuth = false;

const isInvalidBearerAuthorization = (value) => {
  if (typeof value !== "string") {
    return false;
  }

  const normalizedValue = value.trim();

  if (!/^Bearer\b/i.test(normalizedValue)) {
    return false;
  }

  const [, token = ""] = normalizedValue.split(/\s+/, 2);
  return !token || token === "undefined" || token === "null";
};

const configureAxiosAuth = () => {
  if (hasConfiguredAxiosAuth) {
    return;
  }

  axios.interceptors.request.use((config) => {
    if (!isInternalBuzzVibeRequest(config.url)) {
      return config;
    }

    const token = getStoredAuthToken();
    const headers = config.headers || {};
    const existingAuthorization = headers.Authorization || headers.authorization;

    if (isInvalidBearerAuthorization(existingAuthorization)) {
      delete headers.Authorization;
      delete headers.authorization;
    }

    if (!headers.Authorization && !headers.authorization) {
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return {
      ...config,
      withCredentials: true,
      headers,
    };
  });

  hasConfiguredAxiosAuth = true;
};

configureAxiosAuth();

const ThemeLoader = ({ children }) => {
  const dispatch = useDispatch();
  const { theme } = useSelector((state) => state.theme);

  useEffect(() => {
    // 1) Load theme from DB on initial mount
    dispatch(loadTheme());
  }, [dispatch]);

  useEffect(() => {
    // 2) If theme is loaded, set all CSS variables
    if (theme && Object.keys(theme).length > 0) {
      // Apply each field as a CSS variable
      Object.entries(theme).forEach(([key, value]) => {
        document.documentElement.style.setProperty(`--${key}`, value);
      });

      // If there's a siteLogoUrl, set that
      if (theme.siteLogoUrl) {
        document.documentElement.style.setProperty(
          "--site-logo-url",
          `url(${theme.siteLogoUrl})`
        );
      }

      // 3) Toggle dark-mode or light-mode based on isDarkMode
      // (If isDarkMode is not defined, fallback to false => light mode)
      const useDark = theme.isDarkMode === true;

      document.body.classList.toggle("dark-mode", useDark);
      document.body.classList.toggle("light-mode", !useDark);
    }
  }, [theme]);

  return children;
};

ReactDOM.render(
  <Provider store={Store}>
    <ThemeLoader>
      <PrivateLaunchGate>
        <App />
      </PrivateLaunchGate>
    </ThemeLoader>
  </Provider>,
  document.getElementById("root")
);

reportWebVitals();

